/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useRef, useEffect, useState, useMemo } from "react";

// porp-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-chartjs-2 components
// import { Chart as ChartJS, registerables } from 'chart.js';
// import { Chart } from 'react-chartjs-2'
// ChartJS.register(...registerables);
// import 'chartjs-adapter-moment';

// @mui material components
import Card from "@mui/material/Card";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";
import SoftSelect from "components/SoftComponents/SoftSelect";

import DashboardLayout from "template/examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "template/examples/Navbars/DashboardNavbar";
import Footer from "template/examples/Footer";

// Soft UI Dashboard PRO React helper functions
import gradientChartLine from "assets/theme/functions/gradientChartLine";

// Soft UI Dashboard PRO React base styles
import colors from "assets/theme/base/colors";

import Box from '@mui/material/Box';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';

//Backend access
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { getLogs, loadToken } from 'services/api/bks';

import { Route, Link, Routes, useLocation, useNavigate, useMatch } from 'react-router-dom';
import moment from "moment";
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers";

import GradientMinMaxLineChart from "components/Charts/GradientMinMaxLineChart/stats"

import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";

function DeviceStatsChart({ uuid, field, startTime, setStartTime, endTime, setEndTime, groupSize, setGroupSize, height }) {
  // console.log(groupSize)
  loadToken(useAuth0());

  const chartRef = useRef(null);
  const [chartData, setChartData] = useState(null);
  const [min_value, setMinChartValue] = useState({});
  const [max_value, setMaxChartValue] = useState({});
  const [loading, setLoading] = useState(true);

  async function processChartData(data, uuid) {
    var datasets = {
      "min": [],
      "max": [],
      "mean": []
    };

    for (var index in data) {
      var device = data[index];
      for (var value in device.data) {

        var gap = device.data[value].start / device.data[value]["_id"];

        if (typeof field.transform_data == 'function') {
          device.data[value] = field.transform_data(uuid, device.data[value]);
        }

        var timeslot = device.data[value].start + gap;

        if (timeslot > moment().unix()) {
          timeslot = moment().unix();
        }

        for (var key in datasets) {
          datasets[key].push({
            'y': device.data[value][key],
            'x': timeslot * 1000
          });
        }
      }
    }

    var max = Math.max(...datasets.max.map(value => value.y));
    var min = Math.min(...datasets.min.map(value => value.y));

    const chart_data = {
      labels: [],
      datasets: [
        {
          label: "Lower Range",
          backgroundColor: applyGradient,
          borderColor: applyGradient,
          data: datasets.min,
          fill: 1,
          type: 'line',
          radius: 0,
          order: 2,
          tension: 0.3,
          categoryPercentage: 1.0,
          barPercentage: 1.0,
          borderSkipped: false
        },
        {
          label: "Upper Range",
          backgroundColor: applyGradient,
          borderColor: applyGradient,
          data: datasets.max,
          fill: 1,
          type: 'line',
          radius: 0,
          order: 2,
          tension: 0.3,
          categoryPercentage: 1.0,
          barPercentage: 1.0,
          borderSkipped: false

        },
        {
          label: 'Average',
          data: datasets.mean,
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          borderColor: "rgba(0, 0, 0, 0.4)",
          type: 'line',
          order: 1,
          tension: 0.3,
          pointRadius: 3,
          pointHoverRadius: 10,
        }
      ]
    };

    return {
      chart_data, min, max
    }

  }

  useEffect(() => {
    async function fetchData() {
      setLoading(true);

      // console.log(uuid, field.server_name, startTime.unix(), endTime.unix(), groupSize);

      var data = await getLogs(uuid, field.server_name, startTime.unix(), endTime.unix(), groupSize);
      // console.log(data);
      var { chart_data, min, max } = await processChartData(data, uuid);
      setChartData(chart_data);
      setMinChartValue(min);
      setMaxChartValue(max);
      setLoading(false);
    };
    fetchData();
  }, [startTime, endTime, field]);


  var applyGradient = (context) => {
    if (!context.chart.chartArea) {
      return;
    }
    const { ctx, chartArea: { top, bottom } } = context.chart;
    var gradientBg = ctx.createLinearGradient(0, top, 0, bottom);

    field.gradient(gradientBg);
    return gradientBg;
  }

  return (
    <Card>
      <SoftBox p={{ xxs: 1, xs: 2 }}>
        <SoftBox sx={{ opacity: loading ? 0.3 : 1 }}>
          <SoftBox m={1} display="flex" justifyContent="center" alignItems="center">
            <SoftTypography variant="h6">
              {field.label}
            </SoftTypography>
          </SoftBox>
          <SoftBox sx={{ height }}>
            {chartData && <GradientMinMaxLineChart chartData={chartData} field={field} min={field.min} min_value={min_value} max={field.max} max_value={max_value} startTime={startTime.unix() * 1000} endTime={endTime.unix() * 1000} groupSize={groupSize} setStartTime={setStartTime} setEndTime={setEndTime} setGroupSize={setGroupSize} />}
          </SoftBox>
        </SoftBox>
      </SoftBox>
    </Card>
  );

}

// Typechecking props for the DeviceStatsChart
DeviceStatsChart.propTypes = {
  uuid: PropTypes.string,
  field: PropTypes.object,
  startTime: PropTypes.instanceOf(moment()),
  setStartTime: PropTypes.func,
  endTime: PropTypes.instanceOf(moment()),
  setEndTime: PropTypes.func,
  groupSize: PropTypes.number,
  setGroupSize: PropTypes.func,
  height: PropTypes.number
};

export default DeviceStatsChart;

